// AppRoutes.jsx
import { Routes, Route, Navigate } from "react-router-dom";
import { useBeta } from "./context/BetaContext";
import { useDeviceType } from "./hooks/useDeviceType"; // Import the hook
import React from "react";
import "./context/styles/themes.css";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from 'framer-motion';
import "react-toastify/dist/ReactToastify.css";

import PublicLayout from "./Layout/PublicLayout";
import PrivateLayout from "./Layout/PrivateLayout";
import Error from "./components/common/Error";
import { Landing } from "./components/Pages/PublicLayout/LandingPage/LandingPage";

// Public page layout  
import { SignInPage } from "./components/Pages/PublicLayout/SigninPage/Signin";
import { SignInPageEx } from "./components/Pages/PublicLayout/SigninPage/Signin-experience";
// import Login from "./components/Pages/PublicLayout/Loginpage/Login";
// import LoginSampling from "./components/Pages/PublicLayout/Loginpage/Login-sampling";
import Signup from "./components/Pages/PublicLayout/Signuppage/Signup";
import SignoutPage from "./components/Pages/PublicLayout/SignoutPage/SignoutPage";
import JoinPage from "./components/Pages/PublicLayout/JoinPage/joinPage";
import JoinSocietyPage from "./components/Pages/PublicLayout/JoinSocietyPage/joinSocietyPage";
import PrivacyPolicy from "./components/Pages/PublicLayout/PrivacyPolicy/PrivacyPolicy";

import UserFeed from "./components/Pages/AccountPage/UserAccountGridLayout";
import CenterOnlyLayout from "./components/Pages/AccountPage/CenterOnlyLayout"; // Mobile version
import UGPageGridLayout from "./components/Pages/AccountPage/Components/Group/UGGridLayout";
import FriendsPageGrid from "./components/Pages/AccountPage/Components/Friends/FriendsGridLayout";
// import ProfileGroupPage from "./components/Pages/GroupPage/PGPGridLayout";

import CreateProfile from "./components/Pages/PublicLayout/CreateProfile/CreateProfile";
import DeleteProfile from "./components/Pages/PublicLayout/DeleteProfile/DeleteProfile";
import ArchiveProfile from "./components/Pages/PublicLayout/ArchiveProfile/ArchiveProfile";
import ReelsPageUAGridLayout from "./components/Pages/AccountPage/Components/Reels/UAReelsGridLayout";
import ExplorePageUAGridLayout from "./components/Pages/AccountPage/Components/Explore/UAExploreGridLayout";
// import ExplorePageUAGridLayout from "components/Pages/CommonProfileService/Components/Explore/CPSEGridLayout";
// import UserPageBaseLayout from "./components/Pages/UserPage/UserPage";
// import CPSGridLayout from "./components/Pages/CommonProfileService/CPSGridLayout";
// import CPSSGridLayout from "./components/Pages/CommonProfileService/CPSSGridLayout";
import PostPage from "./components/Pages/PostPage/PPGridLayout";

import Settings from "./components/Pages/common/Settings/Settings";
import UpdatePassword from "./components/Pages/common/Settings/Layout/UpdatePassword";
import MediaDisplay from "./components/Pages/common/Settings/Layout/MediaDisplay";
import SecuritySettings from "./components/Pages/common/Settings/Layout/SecuritySettings";
import ParentalControl from "./components/Pages/common/Settings/Layout/ParentalControl";
import PrivacySettings from "./components/Pages/common/Settings/Layout/PrivacySettings";
import GetSubscription from "./components/Pages/common/Settings/Layout/GetSubscription";
import SelectThemes from "./components/Pages/common/Settings/Layout/SelectThemes";
import DeleteAccount from "./components/Pages/common/Settings/Layout/DeleteAccount";
import RewardCenterDashboard from "./components/Pages/common/Settings/Layout/RewardCenter";
import ProfileView from "./components/Pages/common/Settings/Layout/ProfileView";

import ProtectedRoute from "./utils/ProtectedRoute";
import UserAccount from "./components/Pages/UserAccountPage/UserAccount";
import ProfileCreateStatus from "./components/Pages/PublicLayout/ProfileCreateStatus/ProfileCreateStatus";

// ─────────────────────────────────────────────────────────────
// BETA HAND-OFF
// /beta is served by a separate app (src-beta/), which src/index.js mounts
// on a full page load. If the demo router ever reaches /beta (e.g. through a
// <Link to="/beta"> or navigate("/beta")), reload the page so index.js can
// mount the beta app. Only registered when the flag is on; otherwise /beta
// falls through to the demo's catch-all (no reload loop).
// ─────────────────────────────────────────────────────────────
const BETA_ENABLED = process.env.REACT_APP_JOIN_ENABLED === "true";

const BetaHandOff = () => {
  const location = useLocation();
  React.useEffect(() => {
    window.location.replace(location.pathname + location.search + location.hash);
  }, [location]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<PrivateLayout />}></Route>
      </Routes>
    </AnimatePresence>
  );
};

function AppRoutes() {
  const { isBeta } = useBeta();
  const { isMobile } = useDeviceType(); // Get device type

  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signin-ex" element={<SignInPageEx />} />

        {/* <Route
          path="/signin"
          element={isBeta ? <SignInPage /> : <SignInPage />}
        /> */}
        {/* <Route path="/signin" element={<signin />} /> */}
        <Route path="/signout" element={<SignoutPage />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/join-page" element={<JoinPage />} />
        <Route path="/join-society-page" element={<JoinSocietyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Route>

      {/* Protected Routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<PrivateLayout />}>
          {/* ✅ Device-specific UserFeed: CenterOnlyLayout for mobile */}
          <Route
            path="/user-feed/:userId/:profileid?/:action?"
            element={isMobile ? <CenterOnlyLayout /> : <UserFeed />}
          />

          {/* ✅ Only accessible if NOT beta */}
          {!isBeta && (
            <>
              <Route path="/user-friends/:userid" element={<FriendsPageGrid />} />
              <Route path="/user-groups/:userid" element={<UGPageGridLayout />} />
              <Route path="/useraccount-reels/:userid" element={<ReelsPageUAGridLayout />} />
              <Route path="/useraccount-explore/:userid" element={<ExplorePageUAGridLayout />} />
              {/* <Route
                path="/profile-service/:userid/:profileId/:operationType"
                element={<CPSGridLayout />}
              />
              <Route
                path="/profile-service/:userid/:operationType"
                element={<CPSSGridLayout />}
              /> */}
              <Route path="/create-space-success/:userid?" element={<ProfileCreateStatus />} />
              <Route path="/create-profile" element={<CreateProfile />} />
              <Route path="/delete-profile" element={<DeleteProfile />} />
              <Route path="/archive-profile" element={<ArchiveProfile />} />

              <Route path="/user-account/:suserid/:tuserid" element={<UserAccount />} />

              {/* User page view */}
              {/* <Route
                path="/user-page/:suserid?/:tuserid/:profiletype"
                element={<UserPageBaseLayout />}
              /> */}

              <Route path="/post-page/:postid" element={<PostPage />} />

              {/* Settings */}
              <Route path="/" element={<Navigate to="/settings" />} />
              <Route path="settings/:userid" element={<Settings />}>
                <Route path="selectThemes" element={<SelectThemes />} />
                <Route path="mediadisplay" element={<MediaDisplay />} />
                <Route path="profileView" element={<ProfileView />} />
                <Route path="password" element={<UpdatePassword />} />
                <Route path="subscription" element={<GetSubscription />} />
                <Route path="parentalControl" element={<ParentalControl />} />
                <Route path="security" element={<SecuritySettings />} />
                <Route path="privacy" element={<PrivacySettings />} />
                <Route path="deleteaccount" element={<DeleteAccount />} />
                <Route path="rewardcenter" element={<RewardCenterDashboard />} />
              </Route>
            </>
          )}
        </Route>
      </Route>

      {/* Beta app (src-beta/) — full page load so src/index.js mounts it */}
      {BETA_ENABLED && <Route path="/beta/*" element={<BetaHandOff />} />}

      {/* Catch-all */}
      <Route path="*" element={<Error />} />
    </Routes>
  );
}

export default AppRoutes;